import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Wallet,
  Package,
  AlertTriangle,
  Calendar,
  ShoppingCart,
  RefreshCw,
  ArrowRight,
  TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { motion } from "framer-motion";

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/dashboard`);
      setData(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des Dashboards");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("de-DE", { 
      day: "2-digit", 
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Übersicht über den Snackautomaten
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Cash Balance */}
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Kassenbestand</p>
                <p className="text-3xl font-bold text-emerald-700 mt-1">
                  {data?.cash_balance?.toFixed(2) || "0.00"}€
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  +{data?.today_sales?.toFixed(2) || "0.00"}€ heute
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products */}
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Produkte</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">
                  {data?.products_count || 0}
                </p>
                <p className="text-xs text-blue-600 mt-1">im Sortiment</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Low Stock */}
        <Card className={`border-amber-200 ${data?.low_stock_count > 0 ? 'bg-gradient-to-br from-amber-50 to-white' : 'bg-white'}`}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Niedriger Bestand</p>
                <p className={`text-3xl font-bold mt-1 ${data?.low_stock_count > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                  {data?.low_stock_count || 0}
                </p>
                <p className="text-xs text-amber-600 mt-1">nachfüllen</p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${data?.low_stock_count > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                <AlertTriangle className={`w-6 h-6 ${data?.low_stock_count > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expiring Soon */}
        <Card className={`border-red-200 ${data?.expiring_soon > 0 ? 'bg-gradient-to-br from-red-50 to-white' : 'bg-white'}`}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">MHD kritisch</p>
                <p className={`text-3xl font-bold mt-1 ${data?.expiring_soon > 0 ? 'text-red-700' : 'text-slate-400'}`}>
                  {data?.expiring_soon || 0}
                </p>
                <p className="text-xs text-red-600 mt-1">≤7 Tage</p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${data?.expiring_soon > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                <Calendar className={`w-6 h-6 ${data?.expiring_soon > 0 ? 'text-red-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Schnellaktionen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/bestellungen">
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Neue Bestellung erstellen
                </span>
                {data?.open_orders > 0 && (
                  <Badge variant="secondary">{data.open_orders} offen</Badge>
                )}
              </Button>
            </Link>
            
            <Link to="/wareneingang">
              <Button variant="outline" className="w-full justify-start">
                <Package className="w-4 h-4 mr-2" />
                Wareneingang buchen
              </Button>
            </Link>
            
            <Link to="/auffuellen">
              <Button variant="outline" className="w-full justify-start">
                <RefreshCw className="w-4 h-4 mr-2" />
                Automat auffüllen
              </Button>
            </Link>
            
            <Link to="/lager">
              <Button variant="outline" className="w-full justify-start">
                <TrendingUp className="w-4 h-4 mr-2" />
                Lagerbestand prüfen
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Refills */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Letzte Auffüllungen</CardTitle>
            <Link to="/auffuellen">
              <Button variant="ghost" size="sm">
                Alle <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {data?.recent_refills?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_refills.map((refill, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <RefreshCw className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{refill.product_name}</p>
                        <p className="text-xs text-slate-500">
                          Fach {refill.slot_id} • +{refill.quantity} Stück
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">
                      {formatDate(refill.refilled_at)}
                    </span>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">
                Noch keine Auffüllungen
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Workflow Overview */}
      <Card className="border-slate-200 bg-slate-50">
        <CardHeader>
          <CardTitle>Arbeitsablauf</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center">
            <div className="flex-1 p-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-medium">1. Bestellen</p>
              <p className="text-sm text-slate-500">Bestellvorschläge prüfen</p>
            </div>
            
            <ArrowRight className="w-6 h-6 text-slate-300 hidden md:block" />
            
            <div className="flex-1 p-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <p className="font-medium">2. Wareneingang</p>
              <p className="text-sm text-slate-500">Lieferung ins Lager</p>
            </div>
            
            <ArrowRight className="w-6 h-6 text-slate-300 hidden md:block" />
            
            <div className="flex-1 p-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                <RefreshCw className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="font-medium">3. Auffüllen</p>
              <p className="text-sm text-slate-500">Lager → Automat</p>
            </div>
            
            <ArrowRight className="w-6 h-6 text-slate-300 hidden md:block" />
            
            <div className="flex-1 p-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
                <Wallet className="w-6 h-6 text-amber-600" />
              </div>
              <p className="font-medium">4. Verkauf</p>
              <p className="text-sm text-slate-500">Kasse führen</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
